import React, { useContext } from 'react';
import { set } from 'lodash';

import { DomainError } from 'src/models/DomainError';
import { OrgConfigContext } from 'src/presentation/components/pages/integrations/editorComponents/OrgConfigContext';
import { ExternalSource, ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/integrationsResource';
import { ChangeHandler, NameValueList, ResetErrorFunction } from 'src/presentation/components/pages/integrations/types';

import renderAwsEventBridgeConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderAwsEventBridgeConfiguration';
import renderCustomAdapterConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderCustomConfiguration';
import renderBigCommerceConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderBigCommerceConfiguration';
import renderSegmentConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderSegmentConfiguration';
import renderLoopReturnsConfigurations from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderLoopReturnsConfiguration';
import renderMagentoConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderMagentoConfiguration';
import RenderOrdergrooveConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderOrdergrooveConfiguration';
import RenderOrdergrooveConfigurationLegacy from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderOrdergrooveConfigurationLegacy';
import renderQualtricsConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderQualtricsConfiguration';
import renderRechargeConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderRechargeConfiguration';
import renderShopifyConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderShopifyConfiguration';
import renderSmileConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderSmileConfiguration';
import renderYotpoConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderYotpoConfiguration';
import renderZaiusConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/RenderZaiusConfiguration';
import KlaviyoConfiguration from 'src/presentation/components/pages/integrations/editorComponents/editors/KlaviyoConfiguration';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

export type ConfigurationEditorProps<T extends ExternalSource> = {
  integrationType: ExternalSourceType;
  formWorkingState: React.MutableRefObject<T>;
  headerListWorkingState: React.MutableRefObject<NameValueList>; // "Sidecar" Ref to keep the custom header working array
  resetError: ResetErrorFunction;
  errors?: DomainError[];
  maskedFields?: string[]; // List of "secret" fields that need to be masked
  maskedHeaders?: string[]; // List of custom headers that need to be masked
};

const ConfigurationEditor = <T extends ExternalSource>(props: ConfigurationEditorProps<T>): JSX.Element | null => {
  const orgConfig = useContext(OrgConfigContext);
  const isFeatureEnabled = useIsFeatureEnabled();
  const { integrationType, formWorkingState, headerListWorkingState, errors, resetError, maskedFields, maskedHeaders } =
    props;

  const fieldChangeHandler =
    (attr: string): ChangeHandler =>
    value => {
      resetError({ attr, errors });
      set(formWorkingState.current, attr, value);
    };

  const headerListChangeHandler = (newList: NameValueList): void => {
    headerListWorkingState.current = newList;
  };

  switch (integrationType) {
    case ExternalSourceTypes.Shopify:
      return renderShopifyConfiguration(
        fieldChangeHandler,
        maskedFields || [],
        !orgConfig?.isShopifyPrivateAppCustomer
      );

    case ExternalSourceTypes.LoopReturns:
      return renderLoopReturnsConfigurations(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.Magento:
      return renderMagentoConfiguration(fieldChangeHandler, maskedFields || [], ExternalSourceTypes.Magento);

    case ExternalSourceTypes.BigCommerce:
      return renderBigCommerceConfiguration(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.Klaviyo:
      return <KlaviyoConfiguration onChange={fieldChangeHandler} maskedFields={maskedFields || []} />;

    case ExternalSourceTypes.Segment:
      return renderSegmentConfiguration(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.AwsEventBridge:
      return renderAwsEventBridgeConfiguration(fieldChangeHandler);

    case ExternalSourceTypes.Ordergroove:
      if (isFeatureEnabled('ordergrooveApiKey')) {
        return RenderOrdergrooveConfiguration(fieldChangeHandler, maskedFields || []);
      }
      return RenderOrdergrooveConfigurationLegacy(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.Recharge:
      return renderRechargeConfiguration(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.Smile:
      return renderSmileConfiguration(fieldChangeHandler, maskedFields || []);

    case ExternalSourceTypes.Qualtrics:
      return renderQualtricsConfiguration(fieldChangeHandler);

    case ExternalSourceTypes.Yotpo:
      return renderYotpoConfiguration(fieldChangeHandler);

    case ExternalSourceTypes.Zaius:
      return renderZaiusConfiguration(fieldChangeHandler);

    case ExternalSourceTypes.Custom:
      return renderCustomAdapterConfiguration(
        headerListWorkingState.current,
        fieldChangeHandler,
        headerListChangeHandler,
        maskedFields || [],
        maskedHeaders || []
      );

    default:
      return null;
  }
};

export default ConfigurationEditor;
